import { gregorianDate } from "@/utils/gregorianDate.js";
import {
  REVIEW_REQUEST_OPTIONS,
  REVIEW_SECTION_GIFT_CARD,
  REVIEW_SECTION_OTHER,
} from "@/constant/customerReview.js";

export function toReviewTimestamp(value) {
  if (value === null || value === undefined || value === "") return null;
  const timestamp = Number(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function formatReviewTimestamp(value) {
  const timestamp = toReviewTimestamp(value);
  if (timestamp === null) return "";
  return gregorianDate(timestamp, "DD MMM HH:mm");
}

function matchOptionValue(options, value) {
  if (value === null || value === undefined || value === "") return null;
  const match = options.find(
    (option) => option.value.toLowerCase() === String(value).toLowerCase(),
  );
  return match ? match.value : value;
}

export function mapCustomerReview(review) {
  const clientID =
    review?.clientID ?? review?.client?.id ?? review?.userID ?? null;
  const id = review?.id ?? review?.sessionID ?? null;
  const requestValue =
    review?.requestKind ??
    (typeof review?.request === "string" ? review.request : null);
  return {
    ...review,
    id,
    sessionID: review?.sessionID ?? id,
    clientID,
    userID: review?.userID ?? clientID,
    agentID: review?.agentID ?? review?.agent?.id ?? null,
    requestKind: matchOptionValue(REVIEW_REQUEST_OPTIONS, requestValue),
    openedAt: toReviewTimestamp(review?.openedAt),
    firstReply: toReviewTimestamp(review?.firstReply),
  };
}

export function isGameFieldDisabled(section, isEdit) {
  if (section === REVIEW_SECTION_GIFT_CARD) return true;
  if (!section && !isEdit) return true;
  return false;
}

export function gamesForReviewSection(section, allGames, serviceGames) {
  if (section === REVIEW_SECTION_GIFT_CARD) return [];
  if (!section || section === REVIEW_SECTION_OTHER) return allGames || [];
  return serviceGames || [];
}

export function withCurrentGameOption(options, game) {
  if (!game) return options;
  if (options.some((item) => item.game === game)) return options;
  return [{ game }, ...options];
}

function emptyToNull(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return typeof value === "string" ? value.trim() : value;
}

function idOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function optionalReviewFields(form) {
  return {
    game: emptyToNull(form.game),
    agentID: idOrNull(form.agentID),
    firstReply: toReviewTimestamp(form.firstReply),
    lossReason: emptyToNull(form.lossReason),
    complaintReason: emptyToNull(form.complaintReason),
    note: emptyToNull(form.note),
  };
}

export function buildAddCustomerReviewPayload(form) {
  const optional = optionalReviewFields(form);
  const payload = {
    clientID: Number(form.clientID),
    type: form.type,
    section: form.section,
    openedAt: Number(form.openedAt),
    outcome: form.outcome,
    request: form.requestKind,
  };
  if (optional.game) payload.game = optional.game;
  if (optional.agentID !== null) payload.agentID = optional.agentID;
  if (optional.firstReply !== null) payload.firstReply = optional.firstReply;
  if (optional.lossReason) payload.lossReason = optional.lossReason;
  if (optional.complaintReason)
    payload.complaintReason = optional.complaintReason;
  if (optional.note) payload.note = optional.note;
  return payload;
}

export function buildUpdateCustomerReviewPayload(form) {
  const optional = optionalReviewFields(form);
  return {
    sessionID: idOrNull(form.sessionID ?? form.id),
    clientID: idOrNull(form.clientID),
    type: emptyToNull(form.type),
    section: emptyToNull(form.section),
    openedAt: toReviewTimestamp(form.openedAt),
    game: optional.game,
    agentID: optional.agentID,
    firstReply: optional.firstReply,
    request: emptyToNull(form.requestKind),
    outcome: emptyToNull(form.outcome),
    lossReason: optional.lossReason,
    complaintReason: optional.complaintReason,
    note: optional.note,
  };
}

export function reviewMatchesQuery(review, query) {
  const keyword = query?.trim().toLowerCase();
  if (!keyword) return true;
  const haystack = [
    review.id,
    review.clientID,
    review.userID,
    review.clientName,
    review.name,
    review.game,
    review.type,
    review.section,
    review.outcome,
    review.requestKind,
    review.lossReason,
    review.complaintReason,
    review.note,
    review.agentID,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(keyword);
}

export function clientSelectionFromReview(review) {
  if (
    review?.client &&
    typeof review.client === "object" &&
    review.client.id != null
  ) {
    return review.client;
  }
  const clientID = review?.clientID ?? review?.userID ?? null;
  if (clientID === null || clientID === undefined || clientID === "")
    return null;
  const name = review?.clientName || review?.name;
  if (!name) return { id: clientID, name: String(clientID) };
  return { id: clientID, name };
}
